import React, { useState, useEffect, Fragment } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/FormField';
import { Dialog, Transition } from '@headlessui/react';
import { collection, getDocs, query, where, doc, updateDoc, getDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { toast } from 'react-hot-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { validateProductEligibility } from '@/services/marketplaceEligibilityService';
import { Input } from '@/components/ui/input';
import { RefreshCw, History, Pencil, Eye, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { auth } from '@/lib/firebase';
import { ChevronDownIcon } from 'lucide-react';

interface ProductSubmission {
  id: string;
  productId: string;
  productName: string;
  sku: string;
  ean: string;
  proposedPrice: number;
  desiredChannel: string;
  supplierId: string;
  supplierName: string;
  submittedBy: string;
  submitterName: string;
  status: 'pending' | 'approved_primary' | 'approved_reserve' | 'rejected';
  createdAt: Date;
  lastPriceInternal?: number;
  externalPrices?: Record<string, number>;
  rejectionReason?: string;
  approvalLogic?: string;
  adminNotes?: string;
  asin?: string;
  isActive?: boolean;
  publishedAt?: Date;
  mainImage?: string;
  suggestedPrice?: number;
  eligibilityType?: string;
  supplierVAT?: string;
  stockQuantity?: number;
  minimumPrice?: number;
  priceHistory?: {
    date: Date;
    oldPrice: number;
    newPrice: number;
    updatedBy: string;
    updatedByName: string;
  }[];
}

interface PriceHistory {
  date: Date;
  oldPrice: number;
  newPrice: number;
  updatedBy: string;
  updatedByName: string;
}

// Interface para produtos agrupados por EAN e fornecedor
interface GroupedProduct {
  ean: string;
  productName: string;
  suppliers: {
    [supplierId: string]: {
      supplierName: string;
      supplierVAT: string;
      channels: {
        [channel: string]: ProductSubmission;
      };
    };
  };
  expanded: boolean;
  supplierExpanded: { [supplierId: string]: boolean };
}

const getMarketplaceLogo = (channel: string) => {
  const channelLowerCase = channel.toLowerCase();
  switch (channelLowerCase) {
    case 'amazon':
      return '/images/amazon.png';
    case 'fnac':
      return '/images/fnac.png';
    case 'worten':
      return '/images/worten.png';
    case 'kuantokusta':
      return '/images/kuantokusta.png';
    default:
      return '';
  }
};

export default function ProductApproval() {
  const [submissions, setSubmissions] = useState<ProductSubmission[]>([]);
  const [groupedProducts, setGroupedProducts] = useState<GroupedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState<ProductSubmission | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [asin, setAsin] = useState('');
  const [baselinker_id, setBaselinker_id] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [newMinPrice, setNewMinPrice] = useState('');
  const [newProposedPrice, setNewProposedPrice] = useState('');
  const [publishedImageUrl, setPublishedImageUrl] = useState('');
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('activeTab') || 'pending');
  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem('searchTerm') || '');
  const [isEditingPrices, setIsEditingPrices] = useState(false);
  const [currentCommission, setCurrentCommission] = useState(0);
  const [currentValueToReceive, setCurrentValueToReceive] = useState(0);
  const [syncLoading, setSyncLoading] = useState(false);
  const [editingPrice, setEditingPrice] = useState<number | null>(null);
  const [editingSubmissionId, setEditingSubmissionId] = useState<string | null>(null);
  const [showHistorySubmissionId, setShowHistorySubmissionId] = useState<string | null>(null);
  const [selectedChannel, setSelectedChannel] = useState(() => localStorage.getItem('selectedChannel') || 'all');
  const [currentPage, setCurrentPage] = useState(() => Number(localStorage.getItem('currentPage')) || 1);
  const ITEMS_PER_PAGE = 25;

  // Contadores para cada categoria
  const getPendingCount = () => {
    return submissions.filter(submission => 
      submission.status === 'pending' || 
      (!submission.isActive && ['approved_primary', 'approved_reserve'].includes(submission.status)) ||
      (!submission.asin && ['approved_primary', 'approved_reserve'].includes(submission.status))
    ).length;
  };

  const getApprovedCount = () => {
    return submissions.filter(submission => 
      ['approved_primary', 'approved_reserve'].includes(submission.status) && 
      submission.isActive === true && 
      !!submission.asin
    ).length;
  };

  const getRejectedCount = () => {
    return submissions.filter(submission => submission.status === 'rejected').length;
  };

  useEffect(() => {
    loadSubmissions();
  }, [activeTab]);

  useEffect(() => {
    localStorage.setItem('searchTerm', searchTerm);
    localStorage.setItem('activeTab', activeTab);
  }, [searchTerm, activeTab]);

  useEffect(() => {
    localStorage.setItem('currentPage', currentPage.toString());
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab]);

  const loadSubmissions = async () => {
    setLoading(true);
    try {
      const submissionsRef = collection(db, 'marketplaceProductSubmissions');
      let q = submissionsRef;
      
      if (activeTab === 'pending') {
        q = query(submissionsRef, 
          where('status', 'in', ['pending', 'approved_primary', 'approved_reserve'])
        );
      } else if (activeTab === 'approved') {
        q = query(submissionsRef, where('status', 'in', ['approved_primary', 'approved_reserve']));
      } else if (activeTab === 'rejected') {
        q = query(submissionsRef, where('status', '==', 'rejected'));
      }
      
      const querySnapshot = await getDocs(q);
      const submissionsData: ProductSubmission[] = [];
      
      // Mapa para agrupar produtos principais com suas reservas
      const productGroups = new Map<string, ProductSubmission[]>();
      
      // Buscar VATs e stocks
      const companyIds = new Set(querySnapshot.docs.map(doc => doc.data().submittedBy));
      const productIds = new Set(querySnapshot.docs.map(doc => doc.data().productId));
      
      const companyVATs = new Map<string, string>();
      const productStocks = new Map<string, number>();
      
      // Buscar VATs
      await Promise.all(Array.from(companyIds).map(async (companyId) => {
        const companyRef = doc(db, 'companies', companyId);
        const companySnap = await getDoc(companyRef);
        if (companySnap.exists()) {
          companyVATs.set(companyId, companySnap.data().VAT || '');
        }
      }));
      
      // Buscar stocks
      await Promise.all(Array.from(productIds).map(async (productId) => {
        const productRef = doc(db, 'products', productId);
        const productSnap = await getDoc(productRef);
        if (productSnap.exists()) {
          productStocks.set(productId, productSnap.data().stockQuantity || 0);
        }
      }));
      
      // Primeiro, vamos processar todos os documentos
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const submission: ProductSubmission = {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          supplierVAT: companyVATs.get(data.submittedBy) || '',
          stockQuantity: productStocks.get(data.productId) || 0
        } as ProductSubmission;

        // Agrupar por productId
        const existingGroup = productGroups.get(submission.productId) || [];
        existingGroup.push(submission);
        productGroups.set(submission.productId, existingGroup);
      });

      // Agora vamos ordenar e adicionar à lista final
      productGroups.forEach((group) => {
        // Ordenar o grupo: principal primeiro, depois reservas
        group.sort((a, b) => {
          if (a.status === 'approved_primary') return -1;
          if (b.status === 'approved_primary') return 1;
          return 0;
        });
        submissionsData.push(...group);
      });
      
      // Ordenar por data de criação (mais recentes primeiro)
      submissionsData.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      
      setSubmissions(submissionsData);

      // Agrupar produtos por EAN e fornecedor
      const groupedData: { [ean: string]: GroupedProduct } = {};
      
      submissionsData.forEach(submission => {
        if (!groupedData[submission.ean]) {
          groupedData[submission.ean] = {
            ean: submission.ean,
            productName: submission.productName,
            suppliers: {},
            expanded: false,
            supplierExpanded: {}
          };
        }
        
        if (!groupedData[submission.ean].suppliers[submission.submittedBy]) {
          groupedData[submission.ean].suppliers[submission.submittedBy] = {
            supplierName: submission.submitterName,
            supplierVAT: submission.supplierVAT,
            channels: {}
          };
          groupedData[submission.ean].supplierExpanded[submission.submittedBy] = false;
        }
        
        groupedData[submission.ean].suppliers[submission.submittedBy].channels[submission.desiredChannel] = submission;
      });
      
      setGroupedProducts(Object.values(groupedData));
    } catch (error) {
      console.error('Error loading submissions:', error);
      toast.error('Erro ao carregar submissões');
    } finally {
      setLoading(false);
    }
  };

  // Função para filtrar os produtos agrupados com base no termo de pesquisa e na tab ativa
  const getFilteredGroupedProducts = () => {
    return groupedProducts.filter(group => {
      // Verificar se algum produto no grupo corresponde à tab ativa
      const hasMatchingTab = Object.values(group.suppliers).some(supplier => 
        Object.values(supplier.channels).some(submission => {
          if (activeTab === 'pending') {
            return submission.status === 'pending' || 
              (!submission.isActive && ['approved_primary', 'approved_reserve'].includes(submission.status)) ||
              (!submission.asin && ['approved_primary', 'approved_reserve'].includes(submission.status));
          } else if (activeTab === 'approved') {
            return ['approved_primary', 'approved_reserve'].includes(submission.status) && 
              submission.isActive === true && 
              !!submission.asin;
          } else if (activeTab === 'rejected') {
            return submission.status === 'rejected';
          }
          return false;
        })
      );

      if (!hasMatchingTab) return false;

      // Se não houver termo de pesquisa, mostrar todos os grupos que correspondem à tab
      if (!searchTerm) return true;
      
      const searchLower = searchTerm.toLowerCase();
      
      // Verificar se o nome do produto ou EAN contém o termo de pesquisa
      if (
        group.productName.toLowerCase().includes(searchLower) ||
        group.ean.toLowerCase().includes(searchLower)
      ) {
        return true;
      }
      
      // Verificar se algum fornecedor contém o termo de pesquisa
      if (Object.values(group.suppliers).some(supplier => 
        supplier.supplierName.toLowerCase().includes(searchLower)
      )) {
        return true;
      }
      
      // Se nenhuma condição acima for satisfeita, não mostrar o grupo
      return false;
    });
  };

  const filteredSubmissions = submissions.filter(submission => {
    // Primeiro aplicar o filtro por status
    let statusMatch = true;
    if (activeTab === 'pending') {
      // Incluir produtos pendentes E produtos não publicados (sem isActive ou sem ASIN)
      statusMatch = submission.status === 'pending' || 
        (!submission.isActive && ['approved_primary', 'approved_reserve'].includes(submission.status)) ||
        (!submission.asin && ['approved_primary', 'approved_reserve'].includes(submission.status));
    } else if (activeTab === 'approved') {
      // Apenas produtos aprovados que estão publicados (têm isActive e ASIN)
      statusMatch = ['approved_primary', 'approved_reserve'].includes(submission.status) && 
        submission.isActive === true && 
        !!submission.asin;
    } else if (activeTab === 'rejected') {
      statusMatch = submission.status === 'rejected';
    }

    // Por fim aplicar o filtro de busca
    const searchLower = searchTerm.toLowerCase();
    const searchMatch = (
      submission.productName.toLowerCase().includes(searchLower) ||
      submission.ean.toLowerCase().includes(searchLower) ||
      submission.submitterName.toLowerCase().includes(searchLower)
    );

    // Retornar true apenas se todos os filtros passarem
    return statusMatch && searchMatch;
  });

  // Calcular total de páginas
  const totalPages = Math.ceil(filteredSubmissions.length / ITEMS_PER_PAGE);

  // Função para mudar de página
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const handleViewDetails = async (submission: ProductSubmission) => {
    setSelectedSubmission(submission);
    setAdminNotes(submission.adminNotes || '');
    setAsin(submission.asin || '');
    setIsActive(!!submission.isActive);
    setNewMinPrice(submission.minimumPrice?.toString() || '');
    setNewProposedPrice(submission.proposedPrice?.toString() || '');
    
    // Buscar o baselinker_id do produto
    const productRef = doc(db, 'products', submission.productId);
    getDoc(productRef).then((productSnap) => {
      if (productSnap.exists()) {
        setBaselinker_id(productSnap.data().baselinker_id || '');
      }
    });

    setIsDialogOpen(true);
  };

  const handleApprove = async (status: 'approved_primary' | 'approved_reserve') => {
    if (!selectedSubmission) return;
    
    try {
      const submissionRef = doc(db, 'marketplaceProductSubmissions', selectedSubmission.id);
      
      // Verificar se o status solicitado é compatível com a elegibilidade
      if (selectedSubmission.eligibilityType && 
          status === 'approved_primary' && 
          selectedSubmission.eligibilityType !== 'primary') {
        
        // Confirmar com o admin se deseja aprovar como principal mesmo não sendo recomendado
        if (!window.confirm(`Aviso: Este produto foi classificado como '${selectedSubmission.eligibilityType}', não como 'primary'. Deseja realmente aprova-lo como fornecedor principal?`)) {
          return;
        }
      }
      
      await updateDoc(submissionRef, {
        status,
        adminNotes,
        publishedTitle: asin,
        publishedDescription: isActive ? 'active' : '',
        publishedImageUrl,
        reviewedAt: new Date()
      });
      
      toast.success(`Produto ${status === 'approved_primary' ? 'aprovado como fornecedor principal' : 'aprovado como fornecedor reserva'}`);
      setIsDialogOpen(false);
      loadSubmissions();
    } catch (error) {
      console.error('Error approving submission:', error);
      toast.error('Erro ao aprovar submissão');
    }
  };

  const handleReject = async () => {
    if (!selectedSubmission || !rejectionReason) {
      toast.error('É necessário fornecer um motivo para a rejeição');
      return;
    }
    
    try {
      const submissionRef = doc(db, 'marketplaceProductSubmissions', selectedSubmission.id);
      
      await updateDoc(submissionRef, {
        status: 'rejected',
        rejectionReason,
        adminNotes,
        reviewedAt: new Date()
      });
      
      toast.success('Produto rejeitado');
      setIsDialogOpen(false);
      setRejectionReason('');
      loadSubmissions();
    } catch (error) {
      console.error('Error rejecting submission:', error);
      toast.error('Erro ao rejeitar submissão');
    }
  };

  const handlePublish = async () => {
    try {
      // Verificar se o ASIN já existe
      const asinQuery = query(
        collection(db, 'products'),
        where('asin', '==', asin),
        where('productId', '!=', selectedSubmission.productId)
      );
      const asinSnapshot = await getDocs(asinQuery);

      if (!asinSnapshot.empty) {
        toast.error('Este ASIN já está em uso por outro produto');
        return;
      }

      // Atualizar o produto com o ASIN e baselinker_id
      const productRef = doc(db, 'products', selectedSubmission.productId);
      await updateDoc(productRef, {
        asin,
        baselinker_id,
        isActive,
        updatedAt: serverTimestamp()
      });

      // Atualizar a submissão
      const submissionRef = doc(db, 'marketplaceProductSubmissions', selectedSubmission.id);
      await updateDoc(submissionRef, {
        asin,
        isActive,
        publishedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      toast.success('Produto atualizado com sucesso!');
      setIsDialogOpen(false);
      loadSubmissions(); // Recarregar a lista
    } catch (error) {
      console.error('Error publishing product:', error);
      toast.error('Erro ao atualizar o produto');
    }
  };

  const handleUpdatePrices = async () => {
    if (!selectedSubmission) return;
    
    try {
      const submissionRef = doc(db, 'marketplaceProductSubmissions', selectedSubmission.id);
      
      // Format numbers to 2 decimal places to avoid rounding issues
      const newProposedPriceNum = Number(parseFloat(newProposedPrice).toFixed(2));
      const newMinPriceNum = Number(parseFloat(newMinPrice).toFixed(2));
      
      // Calculate commission and value to receive
      const commission = Number((newProposedPriceNum * 0.20).toFixed(2)); // 20% commission
      const valueToReceive = Number((newProposedPriceNum - commission).toFixed(2));
      
      // Create price history entry
      const priceHistoryEntry = {
        date: new Date(),
        oldPrice: selectedSubmission.proposedPrice,
        newPrice: newProposedPriceNum,
        updatedBy: auth.currentUser?.uid || 'admin',
        updatedByName: auth.currentUser?.displayName || auth.currentUser?.email || 'admin'
      };

      const updatedData = {
        minimumPrice: newMinPriceNum,
        proposedPrice: newProposedPriceNum,
        commission,
        valueToReceive,
        priceHistory: [...(selectedSubmission.priceHistory || []), priceHistoryEntry]
      };
      
      await updateDoc(submissionRef, updatedData);
      
      // Update both the selected submission and the submissions list
      const updatedSubmission = {
        ...selectedSubmission,
        ...updatedData
      };
      
      // Update the input fields to show the exact values being saved
      setNewProposedPrice(newProposedPriceNum.toFixed(2));
      setNewMinPrice(newMinPriceNum.toFixed(2));
      
      setSelectedSubmission(updatedSubmission);
      setSubmissions(prevSubmissions => 
        prevSubmissions.map(sub => 
          sub.id === selectedSubmission.id ? updatedSubmission : sub
        )
      );
      
      setIsEditingPrices(false);
      toast.success('Preços atualizados com sucesso');
    } catch (error) {
      console.error('Error updating prices:', error);
      toast.error('Erro ao atualizar preços');
    }
  };

  const handleProposedPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewProposedPrice(value);
    // Format calculations to 2 decimal places
    const price = Number(parseFloat(value || '0').toFixed(2));
    const commission = Number((price * 0.20).toFixed(2)); // 20% commission
    const valueToReceive = Number((price - commission).toFixed(2));
    
    setCurrentCommission(commission);
    setCurrentValueToReceive(valueToReceive);
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return 'Pendente';
      case 'approved_primary': return 'Aprovado (Principal)';
      case 'approved_reserve': return 'Aprovado (Reserva)';
      case 'rejected': return 'Rejeitado';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'text-yellow-600';
      case 'approved_primary': return 'text-green-600';
      case 'approved_reserve': return 'text-blue-600';
      case 'rejected': return 'text-red-600';
      default: return '';
    }
  };

  const getChannelLabel = (channel: string) => {
    switch (channel) {
      case 'amazon': return 'Amazon';
      case 'worten': return 'Worten';
      case 'fnac': return 'FNAC';
      case 'emtuacasa': return 'EMTUACASA';
      default: return channel;
    }
  };

  const renderEligibilityResults = (data: any) => {
    if (!data) return null;

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium">Amazon</h3>
          <span className={`px-3 py-1 rounded-full text-sm ${
            data.eligible ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {data.eligible ? 'Elegível' : 'Não Elegível'}
          </span>
        </div>

        <div className="space-y-3">
          <div className="flex justify-between">
            <span>Buybox:</span>
            <span className="font-medium">{data.buyboxPrice?.toFixed(2)}€</span>
          </div>

          <div className="flex justify-between">
            <span>Total Ofertas:</span>
            <span className="font-medium">{data.totalOffers}</span>
          </div>

          <div className="flex justify-between">
            <span>🇵🇹 Portugal:</span>
            <span className="font-medium">
              {data.ptPrice ? `${data.ptPrice.toFixed(2)}€` : '-'}
            </span>
          </div>

          <div className="flex justify-between">
            <span>🇪🇸 Espanha:</span>
            <span className="font-medium">
              {data.esPrice ? `${data.esPrice.toFixed(2)}€` : '-'}
            </span>
          </div>

          <div className="mt-4 pt-4 border-t">
            <div className="flex justify-between">
              <span>Preço base:</span>
              <span className="font-medium">{data.buyboxPrice?.toFixed(2)}€</span>
            </div>

            <div className="flex justify-between">
              <span>Preço mínimo recomendado:</span>
              <span className="font-medium">{data.recommendedPrice?.toFixed(2)}€</span>
            </div>

            <div className="mt-2">
              <div className="flex justify-between items-center cursor-pointer" onClick={() => setShowCommissionDetails(!showCommissionDetails)}>
                <span>Detalhes da comissão</span>
                <ChevronDownIcon className={`w-5 h-5 transition-transform ${showCommissionDetails ? 'transform rotate-180' : ''}`} />
              </div>

              {showCommissionDetails && (
                <div className="mt-2 space-y-2 pl-4">
                  <div className="flex justify-between text-sm">
                    <span>Comissão base (20%):</span>
                    <span>{data.commission?.toFixed(2)}€</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>IVA sobre comissão (23%):</span>
                    <span>{(data.commissionWithVAT - data.commission).toFixed(2)}€</span>
                  </div>
                </div>
              )}

              <div className="flex justify-between font-medium mt-2">
                <span>Comissão total:</span>
                <span>{data.commissionWithVAT?.toFixed(2)}€</span>
              </div>

              <div className="flex justify-between font-medium mt-2 bg-green-50 p-2 rounded">
                <span>Valor que receberá:</span>
                <span className="text-green-600">{data.valueToReceive?.toFixed(2)}€</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handlePriceSync = async (submission: ProductSubmission) => {
    if (!submission.productId || !submission.proposedPrice) {
      toast.error('Dados insuficientes para sincronização');
      return;
    }

    setSyncLoading(true);
    try {
      // Buscar o baselinker_id do produto
      const productRef = doc(db, 'products', submission.productId);
      const productSnap = await getDoc(productRef);
      
      if (!productSnap.exists()) {
        toast.error('Produto não encontrado');
        return;
      }

      const baselinker_id = productSnap.data().baselinker_id;
      
      if (!baselinker_id) {
        toast.error('ID do Baselinker não encontrado');
        return;
      }

      // Determinar o endpoint baseado no canal
      let endpoint;
      switch (submission.desiredChannel) {
        case 'worten':
          endpoint = '/api/shippix/update_worten_price.php';
          break;
        case 'fnac':
          endpoint = '/api/shippix/update_fnac_price.php';
          break;
        case 'kuantokusta':
          endpoint = '/api/shippix/update_kuantokusta_price.php';
          break;
        default:
          endpoint = '/api/shippix/update_amazon_price.php';
      }

      // Enviar para a API de atualização
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          baselinker_id,
          price: submission.proposedPrice
        })
      });

      const data = await response.json();

      if (response.ok && data.status === 'SUCCESS') {
        toast.success('Preço sincronizado com sucesso');
        
        // Atualizar o preço no Firestore
        const submissionRef = doc(db, 'marketplaceProductSubmissions', submission.id);
        await updateDoc(submissionRef, {
          lastPriceSync: new Date(),
          lastSyncedPrice: submission.proposedPrice
        });

        // Atualizar o produto
        await updateDoc(productRef, {
          [`marketplacePrices.${submission.desiredChannel}`]: submission.proposedPrice,
          lastPriceUpdate: new Date()
        });

        // Atualizar a lista
        loadSubmissions();

      } else {
        throw new Error(data.error_message || data.warnings?.[0] || 'Erro ao sincronizar preço');
      }
    } catch (error) {
      console.error('Erro ao sincronizar preço:', error);
      toast.error('Erro ao sincronizar preço com Baselinker');
    } finally {
      setSyncLoading(false);
    }
  };

  const handlePriceEdit = async (submission: ProductSubmission, newPrice: number) => {
    if (!auth.currentUser) {
      toast.error('Usuário não autenticado');
      return;
    }

    try {
      const oldPrice = submission.proposedPrice;
      const priceHistoryEntry = {
        date: new Date(),
        oldPrice: oldPrice,
        newPrice: newPrice,
        updatedBy: auth.currentUser.uid,
        updatedByName: auth.currentUser.displayName || auth.currentUser.email
      };
      
      // Atualizar submission com novo preço e histórico
      const submissionRef = doc(db, 'marketplaceProductSubmissions', submission.id);
      await updateDoc(submissionRef, {
        proposedPrice: newPrice,
        priceHistory: [...(submission.priceHistory || []), priceHistoryEntry]
      });

      // Atualizar estado local
      setSubmissions(prevSubmissions =>
        prevSubmissions.map(sub =>
          sub.id === submission.id
            ? {
                ...sub,
                proposedPrice: newPrice,
                priceHistory: [...(sub.priceHistory || []), priceHistoryEntry]
              }
            : sub
        )
      );

      toast.success('Preço atualizado com sucesso');
      setEditingPrice(null);
    } catch (error) {
      console.error('Erro ao atualizar preço:', error);
      toast.error('Erro ao atualizar preço');
    }
  };

  const handleRemoveSubmission = async (submission: ProductSubmission) => {
    // Mostrar diálogo de confirmação
    if (window.confirm('Tem certeza que deseja remover este produto?')) {
      try {
        // Remover do Firestore
        await deleteDoc(doc(db, 'marketplaceProductSubmissions', submission.id));
        
        toast.success("Produto removido com sucesso!");

        // Atualizar a lista
        loadSubmissions();
      } catch (error) {
        console.error('Erro ao remover produto:', error);
        toast.error("Erro ao remover produto. Tente novamente.");
      }
    }
  };

  const toggleExpand = (ean: string) => {
    setGroupedProducts(prev => 
      prev.map(group => 
        group.ean === ean ? { ...group, expanded: !group.expanded } : group
      )
    );
  };

  const toggleSupplierExpand = (ean: string, supplierId: string) => {
    setGroupedProducts(prev => 
      prev.map(group => {
        if (group.ean === ean) {
          const updatedSupplierExpanded = { ...group.supplierExpanded };
          updatedSupplierExpanded[supplierId] = !updatedSupplierExpanded[supplierId];
          return { ...group, supplierExpanded: updatedSupplierExpanded };
        }
        return group;
      })
    );
  };

  return (
    <div className="max-w-[1600px] mx-auto px-6 py-8">
      <div className="mb-8 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Produtos para Marketplace</h1>
        <Button 
          className="bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          onClick={() => toast.success('Funcionalidade em desenvolvimento')}
        >
          Sync com BASELINKER
        </Button>
      </div>
      
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <Tabs defaultValue="approved" className="w-full" onValueChange={(value) => setActiveTab(value)}>
          <div className="border-b border-gray-200">
            <div className="flex justify-between items-center px-6 py-4">
              <TabsList className="bg-gray-100">
                <TabsTrigger value="approved" className="data-[state=active]:bg-white">Aprovados ({getApprovedCount()})</TabsTrigger>
                <TabsTrigger value="pending" className="data-[state=active]:bg-white">Pendentes ({getPendingCount()})</TabsTrigger>
                <TabsTrigger value="rejected" className="data-[state=active]:bg-white">Rejeitados ({getRejectedCount()})</TabsTrigger>
              </TabsList>

              <div className="flex items-center gap-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Pesquisar..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <TabsContent value={activeTab} className="mt-0">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-gray-600">Carregando...</span>
                </div>
              </div>
            ) : getFilteredGroupedProducts().length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-500">
                  {searchTerm 
                    ? 'Nenhum resultado encontrado para a sua pesquisa.'
                    : `Nenhuma submissão ${activeTab === 'pending' ? 'pendente' : activeTab === 'approved' ? 'aprovada' : 'rejeitada'} encontrada.`
                  }
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full divide-y divide-gray-200">
                  <thead>
                    <tr className="bg-gray-50">
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[20%]">Produto</th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[12%]">EAN</th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[8%]">Preço</th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[8%]">Preço Min</th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[8%]">Stock</th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%]">Canal</th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[12%]">Fornecedor</th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%]">Status</th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[8%]">Publicado</th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[4%]">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getFilteredGroupedProducts()
                      .slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
                      .map((group) => (
                        <React.Fragment key={group.ean}>
                          {/* Linha principal do produto (EAN) */}
                          <tr 
                            className="hover:bg-gray-50 border-b border-gray-100 cursor-pointer" 
                            onClick={() => toggleExpand(group.ean)}
                          >
                            <td colSpan={10} className="px-4 py-4 whitespace-nowrap">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0 h-10 w-10">
                                    {Object.values(group.suppliers).some(supplier => 
                                      Object.values(supplier.channels).some(channel => channel.mainImage)
                                    ) ? (
                                      <img 
                                        className="h-10 w-10 rounded-full object-cover" 
                                        src={Object.values(group.suppliers)
                                          .flatMap(supplier => Object.values(supplier.channels))
                                          .find(channel => channel.mainImage)?.mainImage || ''} 
                                        alt="" 
                                      />
                                    ) : (
                                      <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                        <span className="text-gray-500 text-xs">Sem img</span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="ml-4">
                                    <div className="text-sm font-medium text-gray-900 max-w-[250px] truncate">
                                      {group.productName}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      EAN: {group.ean}
                                    </div>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="text-gray-400 hover:text-gray-500 transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleExpand(group.ean);
                                  }}
                                >
                                  {group.expanded ? (
                                    <ChevronUp className="w-4 h-4" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4" />
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>

                          {/* Linhas expandidas para fornecedores */}
                          {group.expanded && Object.entries(group.suppliers).map(([supplierId, supplier]) => (
                            <React.Fragment key={supplierId}>
                              {/* Linha do fornecedor */}
                              <tr 
                                className="bg-gray-50 hover:bg-gray-100 cursor-pointer" 
                                onClick={() => toggleSupplierExpand(group.ean, supplierId)}
                              >
                                <td className="px-4 py-4 whitespace-nowrap pl-8">
                                  {/* Célula vazia para a coluna Produto */}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap">
                                  {/* Célula vazia para a coluna EAN */}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap" colSpan={5}>
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                      <div className="text-sm font-medium text-gray-900">
                                        {supplier.supplierName}
                                      </div>
                                      <div className="ml-2 text-xs text-gray-500">
                                        (NIF: {supplier.supplierVAT})
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      className="text-gray-400 hover:text-gray-500 transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleSupplierExpand(group.ean, supplierId);
                                      }}
                                    >
                                      {group.supplierExpanded[supplierId] ? (
                                        <ChevronUp className="w-4 h-4" />
                                      ) : (
                                        <ChevronDown className="w-4 h-4" />
                                      )}
                                    </button>
                                  </div>
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap" colSpan={3}>
                                  {/* Células vazias para as colunas restantes */}
                                </td>
                              </tr>

                              {/* Linhas expandidas para canais do fornecedor */}
                              {group.supplierExpanded[supplierId] && Object.values(supplier.channels).map((submission) => (
                                <tr key={submission.id} className="bg-gray-50 hover:bg-gray-100">
                                  <td className="px-4 py-4 whitespace-nowrap pl-12">
                                    {/* Célula vazia para a coluna Produto */}
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap">
                                    {/* Célula vazia para a coluna EAN */}
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                                    {submission.proposedPrice.toFixed(2)} €
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {submission.minimumPrice ? (
                                      <span className="font-medium">{submission.minimumPrice.toFixed(2)} €</span>
                                    ) : (
                                      <span className="text-yellow-600 text-sm">Não definido</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {submission.stockQuantity || 0}
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                      <img 
                                        src={getMarketplaceLogo(submission.desiredChannel)} 
                                        alt={submission.desiredChannel} 
                                        className="h-5 w-auto mr-2" 
                                      />
                                      <div className="text-sm text-gray-900">{getChannelLabel(submission.desiredChannel)}</div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap">
                                    {/* Célula vazia para a coluna Fornecedor */}
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                                    <span className={`${getStatusColor(submission.status)} truncate inline-block max-w-full`}>
                                      {getStatusLabel(submission.status)}
                                    </span>
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {submission.isActive ? 'Sim' : 'Não'}
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                    <div className="flex items-center gap-1">
                                      <button
                                        onClick={() => handleViewDetails(submission)}
                                        className="p-1.5 hover:bg-gray-50 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
                                        title="Ver detalhes"
                                      >
                                        <Eye className="h-4 w-4" />
                                      </button>
                                      
                                      {/* Botão de histórico de preços */}
                                      {submission.priceHistory && submission.priceHistory.length > 0 && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setShowHistorySubmissionId(showHistorySubmissionId === submission.id ? null : submission.id);
                                          }}
                                          className="p-1.5 hover:bg-gray-50 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
                                          title="Histórico de preços"
                                        >
                                          <History className="h-4 w-4" />
                                        </button>
                                      )}
                                      
                                      {/* Botão de sincronização */}
                                      {submission.status.includes('approved') && submission.isActive && (
                                        <button
                                          onClick={() => handleSyncPrice(submission)}
                                          disabled={syncLoading}
                                          className={`p-1.5 hover:bg-gray-50 rounded-full ${syncLoading ? 'text-gray-300' : 'text-gray-400 hover:text-gray-600'} transition-colors`}
                                          title="Sincronizar preço"
                                        >
                                          <RefreshCw className={`h-4 w-4 ${syncLoading ? 'animate-spin' : ''}`} />
                                        </button>
                                      )}
                                      
                                      {/* Botão de remoção */}
                                      {submission.status === 'pending' && (
                                        <button
                                          onClick={() => handleRemoveSubmission(submission)}
                                          className="p-1.5 hover:bg-gray-50 rounded-full text-red-400 hover:text-red-600 transition-colors"
                                          title="Remover produto"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </React.Fragment>
                          ))}
                        </React.Fragment>
                      ))}
                  </tbody>
                </table>
                <div className="flex justify-between items-center py-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Mostrando {getFilteredGroupedProducts().length} resultados</span>
                    <span className="text-sm text-gray-500">Página {currentPage} de {Math.ceil(getFilteredGroupedProducts().length / ITEMS_PER_PAGE)}</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`p-2 rounded-full text-gray-400 hover:text-gray-600 transition-colors ${
                        currentPage === 1 ? 'bg-gray-200' : 'bg-white'
                      }`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === Math.ceil(getFilteredGroupedProducts().length / ITEMS_PER_PAGE)}
                      className={`p-2 rounded-full text-gray-400 hover:text-gray-600 transition-colors ${
                        currentPage === Math.ceil(getFilteredGroupedProducts().length / ITEMS_PER_PAGE) ? 'bg-gray-200' : 'bg-white'
                      }`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {selectedSubmission && (
        <Transition appear show={isDialogOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setIsDialogOpen(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      Detalhes da Submissão - {selectedSubmission.productName}
                    </Dialog.Title>
                    
                    <div className="mt-4 space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Left Column */}
                        <div>
                          <h3 className="text-lg font-medium mb-4">Informações do Produto</h3>
                          <div className="space-y-4">
                            {selectedSubmission.mainImage && (
                              <div className="mb-4">
                                <img 
                                  src={selectedSubmission.mainImage} 
                                  alt={selectedSubmission.productName} 
                                  className="h-32 w-32 object-cover rounded-md mx-auto"
                                />
                              </div>
                            )}
                            
                            <div>
                              <p className="text-sm font-medium">Produto</p>
                              <p className="text-sm text-gray-500">{selectedSubmission.productName}</p>
                            </div>
                            
                            <div>
                              <p className="text-sm font-medium">EAN</p>
                              <p className="text-sm text-gray-500">{selectedSubmission.ean}</p>
                            </div>

                            <div>
                              <p className="text-sm font-medium">Canal Desejado</p>
                              <p className="text-sm text-gray-500">{getChannelLabel(selectedSubmission.desiredChannel)}</p>
                            </div>
                            
                            <div>
                              <p className="text-sm font-medium">Fornecedor</p>
                              <p className="text-sm text-gray-500">
                                <span className="font-bold">{selectedSubmission.submitterName || 'EMTUACASA'}</span>
                                <span className="block text-xs text-gray-400">
                                  NIF: {selectedSubmission.supplierVAT}
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Right Column */}
                        <div className="space-y-6">
                          <h3 className="text-lg font-medium mb-4">Análise de Preços</h3>
                          <div className="space-y-4 bg-white rounded-lg p-6">
                            <div className="flex flex-col space-y-6">
                              {/* Preço Proposto com edição inline */}
                              <div>
                                <div className="flex items-center justify-between">
                                  <h3 className="text-lg text-gray-700">Preço Proposto</h3>
                                  <div className="flex items-center gap-3">
                                    {editingPrice !== null && editingSubmissionId === selectedSubmission.id ? (
                                      <div className="flex items-center gap-2">
                                        <span className="text-2xl font-medium">€</span>
                                        <Input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          value={editingPrice}
                                          onChange={(e) => setEditingPrice(parseFloat(e.target.value))}
                                          className="w-24 text-2xl font-medium text-right"
                                          onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                              handlePriceEdit(selectedSubmission, editingPrice);
                                              setEditingSubmissionId(null);
                                            }
                                          }}
                                          onBlur={() => {
                                            if (editingPrice !== selectedSubmission.proposedPrice) {
                                              handlePriceEdit(selectedSubmission, editingPrice);
                                            }
                                            setEditingSubmissionId(null);
                                          }}
                                          autoFocus
                                        />
                                      </div>
                                    ) : (
                                      <>
                                        <span className="text-2xl font-medium">€{selectedSubmission.proposedPrice.toFixed(2)}</span>
                                        <div className="flex items-center gap-2">
                                          <button
                                            onClick={() => {
                                              setEditingPrice(selectedSubmission.proposedPrice);
                                              setEditingSubmissionId(selectedSubmission.id);
                                            }}
                                            className="p-2 hover:bg-gray-50 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
                                          >
                                            <Pencil className="h-5 w-5" />
                                          </button>
                                          <button
                                            onClick={() => {
                                              if (showHistorySubmissionId === selectedSubmission.id) {
                                                setShowHistorySubmissionId(null);
                                              } else {
                                                setShowHistorySubmissionId(selectedSubmission.id);
                                              }
                                            }}
                                            className="p-2 hover:bg-gray-50 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
                                          >
                                            <History className="h-5 w-5" />
                                          </button>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>

                                {/* Histórico de Preços */}
                                {showHistorySubmissionId === selectedSubmission.id && selectedSubmission.priceHistory && (
                                  <div className="mt-4 bg-gray-50 rounded-lg p-4">
                                    <h4 className="text-sm font-medium text-gray-900 mb-3">Histórico de Alterações</h4>
                                    <div className="space-y-3">
                                      {selectedSubmission.priceHistory?.slice(-2).map((history, index) => {
                                        // Suporta ambos os formatos de histórico
                                        const isOldFormat = 'oldPrice' in history;
                                        const price1 = isOldFormat 
                                          ? history.oldPrice 
                                          : history.proposedPrice;
                                        const price2 = isOldFormat 
                                          ? history.newPrice 
                                          : history.minimumPrice;
                                        
                                        // Converte o timestamp do Firestore para Date
                                        const date = history.date && 'seconds' in history.date
                                          ? new Date(history.date.seconds * 1000)
                                          : history.date instanceof Date
                                            ? history.date
                                            : new Date(history.date);
                                        
                                        return (
                                          <div key={index} className="flex flex-col space-y-1">
                                            <div className="flex items-center gap-2 text-base">
                                              {isOldFormat ? (
                                                <>
                                                  <span className="text-gray-500">€{price1?.toFixed(2)}</span>
                                                  <span className="text-gray-400">→</span>
                                                  <span className="text-gray-900">€{price2?.toFixed(2)}</span>
                                                </>
                                              ) : (
                                                <>
                                                  <span className="text-gray-500">Proposto: €{price1?.toFixed(2)}</span>
                                                  <span className="text-gray-400">|</span>
                                                  <span className="text-gray-900">Mínimo: €{price2?.toFixed(2)}</span>
                                                </>
                                              )}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                              {date.toLocaleString('pt-PT', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                              })} por {history.updatedByName || history.updatedBy}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Outros preços em grid */}
                              <div className="grid grid-cols-2 gap-4">
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-700">Preço Recomendado</span>
                                  <span className="font-medium text-blue-600">€{selectedSubmission.recommendedPrice?.toFixed(2)}</span>
                                </div>

                                <div className="flex justify-between items-center">
                                  <span className="text-gray-700">Preço Mínimo Aceito</span>
                                  <span className="font-medium">€{selectedSubmission.minimumPrice?.toFixed(2)}</span>
                                </div>

                                <div className="flex justify-between items-center">
                                  <span className="text-gray-700">Preço Buybox</span>
                                  <span className="font-medium">€{selectedSubmission.buyboxPrice?.toFixed(2)}</span>
                                </div>

                                <div className="flex justify-between items-center">
                                  <span className="text-gray-700">Total de Ofertas</span>
                                  <span className="font-medium">{selectedSubmission.totalOffers}</span>
                                </div>

                                {/* Linha divisória */}
                                <div className="col-span-2 border-t border-gray-100 my-2"></div>

                                {/* Preços por País */}
                                <div className="col-span-2">
                                  <h4 className="font-medium text-gray-900 mb-2">Preços por País</h4>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div className="flex justify-between items-center">
                                      <span className="text-gray-700">Portugal (PT)</span>
                                      <span className="font-medium">€{selectedSubmission.ptPrice?.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                      <span className="text-gray-700">Espanha (ES)</span>
                                      <span className="font-medium">€{selectedSubmission.esPrice?.toFixed(2)}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Publication Section */}
                      {(selectedSubmission.status === 'approved_primary' || selectedSubmission.status === 'approved_reserve') && (
                        <div className="border-t pt-6">
                          <h3 className="text-lg font-medium mb-4">Publicação no Marketplace</h3>
                          <div className="space-y-4">
                            <div>
                              <label className="text-sm font-medium text-gray-700">ASIN</label>
                              <Input
                                value={asin}
                                onChange={(e) => setAsin(e.target.value)}
                                placeholder="Ex: B0CP5X8ND2"
                                className="mt-1"
                              />
                            </div>

                            <div>
                              <label className="text-sm font-medium text-gray-700">Baselinker ID</label>
                              <Input
                                value={baselinker_id}
                                onChange={(e) => setBaselinker_id(e.target.value)}
                                placeholder="Ex: 123456789"
                                className="mt-1"
                              />
                            </div>

                            <div className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                              />
                              <label className="text-sm text-gray-700">Produto Ativo</label>
                            </div>

                            <div>
                              <label className="text-sm font-medium text-gray-700">Notas do Administrador</label>
                              <textarea
                                value={adminNotes}
                                onChange={(e) => setAdminNotes(e.target.value)}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                                rows={4}
                              />
                            </div>

                            <button
                              onClick={handlePublish}
                              disabled={!asin || !baselinker_id || !isActive}
                              className={`w-full py-2 px-4 rounded-md text-white font-medium transition-colors ${
                                asin && baselinker_id && isActive 
                                  ? 'bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2' 
                                  : 'bg-gray-300 cursor-not-allowed'
                              }`}
                            >
                              {selectedSubmission.publishedAt ? 'Atualizar Publicação' : 'Publicar no Marketplace'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Footer Buttons */}
                    <div className="mt-6 flex justify-end gap-2">
                      <Button variant="outline" onClick={() => {
                        setIsEditingPrices(false);
                        setIsDialogOpen(false);
                      }}>
                        Fechar
                      </Button>

                      {/* Price Update Button */}
                      {isEditingPrices && (
                        <Button
                          variant="default"
                          onClick={handleUpdatePrices}
                          disabled={!newMinPrice || !newProposedPrice}
                        >
                          Atualizar Preços
                        </Button>
                      )}

                      {/* Approval/Rejection Buttons */}
                      {!isEditingPrices && selectedSubmission?.status === 'pending' && (
                        <>
                          <Button
                            variant="destructive"
                            onClick={handleReject}
                            disabled={!rejectionReason}
                          >
                            Rejeitar Produto
                          </Button>
                          <Button 
                            variant="default"
                            onClick={() => handleApprove('approved_reserve')}
                          >
                            Aprovar como Reserva
                          </Button>
                          <Button 
                            variant="default"
                            onClick={() => handleApprove('approved_primary')}
                          >
                            Aprovar como Principal
                          </Button>
                        </>
                      )}
                      {/* Botão de Sincronização */}
                      {selectedSubmission && (
                        <Button
                          variant="outline"
                          onClick={() => handlePriceSync(selectedSubmission)}
                          disabled={syncLoading}
                          className="flex items-center gap-2"
                        >
                          <RefreshCw className={`h-4 w-4 ${syncLoading ? 'animate-spin' : ''}`} />
                          {syncLoading ? 'Sincronizando...' : 'Sincronizar Preço'}
                        </Button>
                      )}
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      )}
    </div>
  );
}
